import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import api from "../../../utils/api";
import Swal from "sweetalert2";

const ClientFormsManagement = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const navigate = useNavigate();
  const { lang } = useParams();
  const currentLang = lang || 'en';

  const { data: forms = [], isLoading, error } = useQuery({
    queryKey: ["myForms"],
    queryFn: async () => {
      const token = localStorage.getItem("auth_token");
      if (!token) throw new Error("Authentication required");

      const res = await api.get("/auth/my-forms", {
        headers: { Authorization: `Bearer ${token}` },
      });

      return res.data.data.forms || [];
    },
    staleTime: 1000 * 60 * 5,
  });

  const filteredForms = forms.filter((form) => {
    const matchesSearch =
      form.form_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      form.form_code.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === "all" || form.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const getStatusBadge = (status) => {
    const config = {
      completed: { color: "bg-green-100 text-green-800", text: "Completed" },
      "in-progress": { color: "bg-yellow-100 text-yellow-800", text: "In Progress" },
      pending: { color: "bg-blue-100 text-blue-800", text: "Pending" },
    };
    const c = config[status] || { color: "bg-gray-100 text-gray-800", text: "Unknown" };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${c.color}`}>
        {c.text}
      </span>
    );
  };

  const handleDownload = async (responseId, formName) => {
    const token = localStorage.getItem("auth_token");
    if (!token) {
      Swal.fire({
        icon: "error",
        title: "Login Required",
        text: "Please log in to download forms.",
        confirmButtonColor: "#1e40af",
      });
      return;
    }

    // === Show loading state with SweetAlert ===
    Swal.fire({
      title: "Preparing your PDF...",
      text: "Please wait while we generate the document.",
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      // 1. Get PDF URL (optional check)
      const urlRes = await api.get(`/form-answering/sessions/${responseId}/pdf/url`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!urlRes.data.success) {
        throw new Error("PDF not ready");
      }

      // 2. Download the actual PDF
      const downloadRes = await api.get(`/form-answering/sessions/${responseId}/pdf/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });

      // 3. Create and trigger download
      const blob = new Blob([downloadRes.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${formName.replace(/[^a-z0-9]/gi, "_")}_${responseId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // === Success: Close loader + show success ===
      Swal.fire({
        icon: "success",
        title: "Downloaded!",
        text: "Your PDF has been downloaded successfully.",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      // === Close loading state on error ===
      Swal.close();

      const err = error.response?.data;
      if (err?.message?.includes("Payment required")) {
        const paymentData = err.data || {};
        Swal.fire({
          icon: "warning",
          title: "Payment Required",
          html: `
          <p>You need to pay <strong>$${paymentData.amount_required || "0.50"} ${paymentData.currency?.toUpperCase() || "USD"}</strong> to download this PDF.</p>
          <p><strong>Form:</strong> ${formName}</p>
        `,
          showCancelButton: true,
          confirmButtonText: "Pay Now",
          cancelButtonText: "Later",
          confirmButtonColor: "#1e40af",
        }).then((result) => {
          if (result.isConfirmed) {
            navigate(`/${currentLang}/checkout/form/${responseId}`);
          }
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Download Failed",
          text: err?.message || "Could not retrieve PDF. Please try again.",
          confirmButtonColor: "#1e40af",
        });
      }
    }
  };

  const handleReview = (formCode, responseId) => {
    navigate(`/${currentLang}/forms/${formCode}`);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-16">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12 text-red-600">
        <p>Failed to load your forms. Please try again.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">My Forms & Documents</h2>
          <p className="text-gray-600 mt-1">Manage your filled immigration forms</p>
        </div>
        <Link
          to={`/${currentLang}/forms`}
          className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors duration-200"
        >
          Purchase New Form
        </Link>
      </div>

      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <input
            type="text"
            placeholder="Search forms..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="all">All Status</option>
          <option value="completed">Completed</option>
          <option value="in-progress">In Progress</option>
          <option value="pending">Pending</option>
        </select>
      </div>

      <div className="grid grid-cols-1 gap-6">
        {filteredForms.map((form, index) => (
          <motion.div
            key={form.response_id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow duration-200"
          >
            <div className="flex flex-col h-full">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">{form.form_name}</h3>
                    {getStatusBadge(form.status)}
                  </div>
                  <p className="text-gray-600 mb-2">Code: {form.form_code.toUpperCase()}</p>
                </div>
                <div className="text-right">
                  <div className="text-xl font-bold text-gray-900">
                    {form.is_paid ? `$${form.amount_paid || "0.00"}` : "Free"}
                  </div>
                </div>
              </div>

              <div className="flex-1">
                <div className="space-y-2 text-sm text-gray-500 mb-4">
                  <div className="flex justify-between">
                    <span>Started:</span>
                    <span>{new Date(form.started_at).toLocaleDateString()}</span>
                  </div>
                  {form.completed_at && (
                    <div className="flex justify-between">
                      <span>Completed:</span>
                      <span>{new Date(form.completed_at).toLocaleDateString()}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span>Answers:</span>
                    <span>{form.total_answers}</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-2 pt-4 border-t border-gray-100">
                <motion.button
                  onClick={() => handleDownload(form.response_id, form.form_name)}
                  className="flex-1 flex items-center justify-center px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors duration-200"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Download
                </motion.button>

                <motion.button
                  onClick={() => handleReview(form.form_code)}
                  className="flex-1 flex items-center justify-center px-3 py-2 text-sm font-medium text-gray-600 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-200"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  Review
                </motion.button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
      {filteredForms.length === 0 && (
        <div className="text-center py-12">
          <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {forms.length === 0 ? "No forms yet" : "No forms found"}
          </h3>
          <p className="text-gray-600">
            {forms.length === 0
              ? "Start by purchasing your first form."
              : "Try adjusting your search or filter."}
          </p>
        </div>
      )}

      <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-blue-900">Total Forms</p>
              <p className="text-2xl font-bold text-blue-600">{forms.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-green-50 rounded-lg p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-green-900">Completed</p>
              <p className="text-2xl font-bold text-green-600">
                {forms.filter((f) => f.status === "completed").length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-yellow-50 rounded-lg p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-yellow-900">In Progress</p>
              <p className="text-2xl font-bold text-yellow-600">
                {forms.filter((f) => f.status === "in-progress").length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-purple-50 rounded-lg p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-purple-900">Total Spent</p>
              <p className="text-2xl font-bold text-purple-600">
                ${forms
                  .filter((f) => f.is_paid)
                  .reduce((sum, f) => sum + parseFloat(f.amount_paid || 0), 0)
                  .toFixed(2)}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientFormsManagement;